import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import { useUser, useOrganization } from "@clerk/nextjs";
import { type SubscriptionData } from "~/server/api/routers/stripe";
import { api } from "~/trpc/react";
import { type inferRouterOutputs } from "@trpc/server";
import { type AppRouter } from "~/server/api/root";
import { usePathname } from "next/navigation";

type RouterOutput = inferRouterOutputs<AppRouter>;

interface SubscriptionContextType {
  subscription: SubscriptionData | null;
  isContextLoading: boolean;
  error: Error | null;
  refetchSubscription: () => Promise<void>;
  subscriptionData: RouterOutput["stripe"]["getSubscriptionData"];
  subscriptionDataIsLoading: boolean;
  subscriptionPermissions: {
    isAbleToCreateProjects: boolean;
    isAbleToCreateEmailDomains: boolean;
    isAbleToInviteMembers: boolean;
  };
  usage: {
    projects: { current: number; limit: number; label: string };
    emailDomains: { current: number; limit: number; label: string };
    members: { current: number; limit: number; label: string };
  };
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined,
);

// Default values - UNLIMITED ACCESS FOR ALL USERS
const DEFAULT_MAX_GPT_COUNT = 999;
const DEFAULT_MAX_VERIFIED_DOMAIN_COUNT = 999;
const DEFAULT_MAX_MEMBER_COUNT = 999;

// Development override values
const DEV_OVERRIDE = {
  enabled: process.env.NODE_ENV === "development",
  maxGPTCount: 10,
  maxVerifiedDomainCount: 5,
  maxMemberCount: 20,
  currentId: null,
};

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const pathname = usePathname();
  const { organization } = useOrganization();
  const [subscription, setSubscription] = useState<SubscriptionData | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const organizationId = organization?.id;

  const disabledRoutes = ["/w/", "/chat/", "/sign-in", "/sign-up", "/widget-dev-test", "/agent/"];

  // CRITICAL: Only enable queries when user is logged in
  const isUserLoggedIn = !!user;
  
  const { data: projects, isLoading: projectsLoading } =
    api.project.getAllProjects.useQuery(
      { organisationId: organizationId },
      {
        enabled:
          isUserLoggedIn &&
          !!organizationId &&
          !disabledRoutes.some((route) => pathname.startsWith(route)),
        refetchInterval: false, // Don't poll - only fetch when needed
        refetchOnWindowFocus: false, // Don't refetch on focus
        refetchOnMount: isUserLoggedIn && !!organizationId && !disabledRoutes.some((route) => pathname.startsWith(route)), // Only refetch on mount if user is logged in
        refetchOnReconnect: false, // Don't refetch on reconnect
      },
    );

  const { data: emailDomains, isLoading: emailDomainsLoading } =
    api.emailDomains.getAllEmailDomains.useQuery(
      {
        organisationId: organizationId,
      },
      {
        enabled:
          isUserLoggedIn &&
          !!organizationId &&
          !disabledRoutes.some((route) => pathname.startsWith(route)),
        refetchInterval: false, // Don't poll - only fetch when needed
        refetchOnWindowFocus: false, // Don't refetch on focus
        refetchOnMount: isUserLoggedIn && !!organizationId && !disabledRoutes.some((route) => pathname.startsWith(route)), // Only refetch on mount if user is logged in
        refetchOnReconnect: false, // Don't refetch on reconnect
      },
    );

  const currentId = organization?.id ?? user?.id ?? "";

  // Stripe integration removed - return mock data
  const subscriptionData = {
    subscription: null,
    productMetadata: null,
  };
  const subscriptionDataLoading = false;

  const subscriptionPermissions = {
    isAbleToCreateProjects: true,
    isAbleToCreateEmailDomains: true,
    isAbleToInviteMembers: true,
  };

  const usage = {
    projects: { current: 0, limit: DEFAULT_MAX_GPT_COUNT, label: "GPTs" },
    emailDomains: {
      current: 0,
      limit: DEFAULT_MAX_VERIFIED_DOMAIN_COUNT,
      label: "Domains",
    },
    members: {
      current: 1,
      limit: DEFAULT_MAX_MEMBER_COUNT,
      label: "Members",
    },
  };

  if (!projectsLoading && !emailDomainsLoading && !subscriptionDataLoading) {
    const projectsCount = projects?.length ?? 0;
    const emailDomainsCount = emailDomains?.length ?? 0;
    const membersCount = organization?.membersCount ?? 1;

    const maxGPTCount = DEV_OVERRIDE.enabled
      ? DEV_OVERRIDE.maxGPTCount
      : (subscriptionData?.productMetadata?.maxGPTCount ??
        DEFAULT_MAX_GPT_COUNT);
    const maxVerifiedDomainCount = DEV_OVERRIDE.enabled
      ? DEV_OVERRIDE.maxVerifiedDomainCount
      : (subscriptionData?.productMetadata?.maxVerifiedDomainCount ??
        DEFAULT_MAX_VERIFIED_DOMAIN_COUNT);
    const maxMemberCount = DEV_OVERRIDE.enabled
      ? DEV_OVERRIDE.maxMemberCount
      : (subscriptionData?.productMetadata?.maxMemberCount ??
        DEFAULT_MAX_MEMBER_COUNT);

    subscriptionPermissions.isAbleToCreateProjects =
      projectsCount < maxGPTCount;
    subscriptionPermissions.isAbleToCreateEmailDomains =
      emailDomainsCount < maxVerifiedDomainCount;
    subscriptionPermissions.isAbleToInviteMembers =
      !!organizationId && organization.membersCount < maxMemberCount;

    usage.projects = {
      current: projectsCount,
      limit: maxGPTCount,
      label: "GPTs",
    };
    usage.emailDomains = {
      current: emailDomainsCount,
      limit: maxVerifiedDomainCount,
      label: "Domains",
    };
    usage.members = {
      current: membersCount,
      limit: maxMemberCount,
      label: "Members",
    };
  }

  const refetchSubscription = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const subData = organizationId
        ? (organization?.publicMetadata?.subscription as SubscriptionData)
        : (user?.publicMetadata?.subscription as SubscriptionData);
      setSubscription(subData || null);
    } catch (err) {
      setError(err instanceof Error ? err : new Error("An error occurred"));
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, organization, user]);

  useEffect(() => {
    void refetchSubscription();
  }, [refetchSubscription]);

  const isContextLoading =
    isLoading ||
    subscriptionDataLoading ||
    emailDomainsLoading ||
    projectsLoading;

  const value = useMemo(
    () => ({
      subscription,
      isContextLoading,
      error,
      refetchSubscription,
      subscriptionData: subscriptionData ?? null,
      subscriptionDataIsLoading: subscriptionDataLoading,
      subscriptionPermissions,
      usage,
    }),
    [
      subscription,
      isContextLoading,
      error,
      refetchSubscription,
      subscriptionData,
      subscriptionDataLoading,
      subscriptionPermissions,
      usage,
    ],
  );

  return (
    <SubscriptionContext.Provider
      value={{
        ...value,
        subscriptionData: subscriptionData ?? {
          subscription: null,
          productMetadata: null,
        },
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider",
    );
  }
  return context;
}
